import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import fbnLogo from "@/assets/fbn-logo.png";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SignupForm } from "@/components/auth/SignupForm";
import { LoginForm } from "@/components/auth/LoginForm";
import { GrainOverlay } from "@/components/ui/GrainOverlay";

const Auth = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab: "login" | "signup" =
    location.pathname === "/login" ? "login" : "signup";

  const handleTabChange = (v: string) => {
    navigate(v === "login" ? "/login" : "/signup", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      <GrainOverlay />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 lg:px-12 py-4 bg-white border-b border-border">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span>Home</span>
          </Link>

          <img src={fbnLogo} alt="FBN Entrepreneurs" className="h-14 w-auto" />
        </div>
      </nav>

      {/* Main Content */}
      <main id="main-content" className="relative z-10 pt-24 sm:pt-32 pb-16 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Header - hidden on mobile to keep form above the fold */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="hidden sm:block text-center mb-10"
          >
            <h1 className="text-4xl sm:text-5xl font-serif font-semibold text-foreground mb-4">
              {activeTab === "signup" ? "Join the Community" : "Welcome Back"}
            </h1>
            <p className="text-lg text-muted-foreground max-w-md mx-auto">
              {activeTab === "signup"
                ? "Become part of a global community of entrepreneurs from business families, learning and building together across generations."
                : "Log in to access your profile and connect with members."}
            </p>
          </motion.div>

          {/* Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Tabs
              value={activeTab}
              onValueChange={handleTabChange}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2 mb-8">
                <TabsTrigger value="signup" className="text-base">
                  Sign Up
                </TabsTrigger>
                <TabsTrigger value="login" className="text-base">
                  Log In
                </TabsTrigger>
              </TabsList>

              <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-soft">
                <TabsContent value="signup" className="mt-0">
                  <SignupForm />
                </TabsContent>

                <TabsContent value="login" className="mt-0">
                  <LoginForm />
                </TabsContent>
              </div>
            </Tabs>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Auth;
